// src/components/Admin/Talleres/HorariosModal.jsx
//
// Horarios de un taller, sus inscritos y las acciones sobre cada inscripción (cambio
// de horario o taller, baja) y sobre cada horario (cupos, edición). Con el lenguaje
// del panel rediseñado: ver piezasTalleres.jsx.
import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Select, DatePicker, InputNumber,
  Popconfirm, Table, Alert, Input, Switch, Tooltip, Tabs, Spin,
} from 'antd';
import { MedicineBoxOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  FiCalendar, FiCheck, FiClock, FiDownload, FiEdit2, FiFileText, FiGrid,
  FiPlus, FiRepeat, FiSliders, FiTrash2, FiUsers, FiUserX, FiX,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import Horarios from '../../../../service/Horarios';
import InscripcionSvc from '../../../../service/Inscripciones';
import { useWithNotify, useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { SALDO, textoSaldo, pesos } from '../../../../utils/saldoCambioTaller';
import { formatearNombre } from '../../../../utils/texto';
import {
  claseBtnPrimario, claseBtnSecundario, FiltroChips, ChipNeutro, Iniciales,
} from '../../../Ui/Admin/PiezasAdmin';
import {
  Chip, CabeceraModal, ChipCabecera, Kpi, Etiqueta, Paso, Vacio, BarraCupos, claseTituloSeccion,
  claseBtnPeligro, claseBtnChico, claseBtnChicoPrimario, claseBtnChicoPeligro, claseBtnIcono, claseBtnIconoPeligro,
} from './piezasTalleres';

// Saldo del cambio de taller → tono del chip
const TONO_SALDO = { a_favor: 'azul', en_contra: 'rojo', sin_diferencia: 'gris' };

// Una persona "declara condición" cuando lo escrito es distinto de "Ninguna".
const tieneCondicionSalud = (valor) => {
  const v = (valor || '').trim();
  return v !== '' && v.toLowerCase() !== 'ninguna';
};

const DIAS_OPTIONS = [
  { label: 'Lunes',     value: 'lunes'      },
  { label: 'Martes',    value: 'martes'     },
  { label: 'Miércoles', value: 'miércoles'  },
  { label: 'Jueves',    value: 'jueves'     },
  { label: 'Viernes',   value: 'viernes'    },
  { label: 'Sábado',    value: 'sábado'     },
  { label: 'Domingo',   value: 'domingo'    },
];

const DIA_ABBR = {
  lunes: 'Lun', martes: 'Mar', 'miércoles': 'Mié',
  jueves: 'Jue', viernes: 'Vie', 'sábado': 'Sáb', domingo: 'Dom',
};

const diasOrder = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const sortDias = (arr) => [...arr].sort((a, b) => diasOrder.indexOf(a) - diasOrder.indexOf(b));

const validarSolapamientoBloques = (bloques) => {
  for (let i = 0; i < bloques.length; i++) {
    for (let j = i + 1; j < bloques.length; j++) {
      const bloqueA = bloques[i];
      const bloqueB = bloques[j];
      const diasComunes = bloqueA.dias.filter(dia => bloqueB.dias.includes(dia));
      if (diasComunes.length > 0) {
        const seSolapan = (ini1, fin1, ini2, fin2) => {
          const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
          return toMin(ini1) < toMin(fin2) && toMin(ini2) < toMin(fin1);
        };
        if (seSolapan(bloqueA.inicio, bloqueA.fin, bloqueB.inicio, bloqueB.fin)) {
          return { solapa: true, bloqueA, bloqueB, diasComunes };
        }
      }
    }
  }
  return { solapa: false };
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const HorariosModal = ({ visible, taller, onClose, onEdit, onHorariosChange }) => {
  const [editForm]   = Form.useForm();
  const [createForm] = Form.useForm();
  const [cuposForm]  = Form.useForm();

  const [horarios,              setHorarios]              = useState([]);
  const [loading,               setLoading]               = useState(false);
  const [editingId,             setEditingId]             = useState(null);
  const [activeTab,             setActiveTab]             = useState('vista');
  const [bloques,               setBloques]               = useState([]);
  const [modalInscritosVisible, setModalInscritosVisible] = useState(false);
  const [horarioSeleccionado,   setHorarioSeleccionado]   = useState(null);
  const [inscritosData,         setInscritosData]         = useState(null);
  const [loadingInscritos,      setLoadingInscritos]      = useState(false);
  const [loadingExport,         setLoadingExport]         = useState(false);
  const [filtroEstado,          setFiltroEstado]          = useState('');
  const [filtroVigente,         setFiltroVigente]         = useState('');
  const [bajaModal,             setBajaModal]             = useState({ visible: false, inscripcion: null });
  const [loadingBaja,           setLoadingBaja]           = useState(false);
  const [bloquesEdicion,        setBloquesEdicion]        = useState([]);
  const [modoEdicionExpandida,  setModoEdicionExpandida]  = useState(false);
  const [editandoBloqueId,      setEditandoBloqueId]      = useState(null);
  const [cuposModal,            setCuposModal]            = useState({ visible: false, horario: null });
  const [loadingCupos,          setLoadingCupos]          = useState(false);
  const [cambioModal,           setCambioModal]           = useState({ visible: false, inscripcion: null });
  const [nuevoHorarioId,        setNuevoHorarioId]        = useState(null);
  const [loadingCambio,         setLoadingCambio]         = useState(false);
  // Cambio a otro taller: 'actual' = cambiar de horario dentro de este taller
  const [tallerDestinoId,       setTallerDestinoId]       = useState('actual');
  const [opcionesCambio,        setOpcionesCambio]        = useState({ loading: false, data: null });

  const withNotify = useWithNotify();
  const notify     = useNotify();
  const { user }   = useAuthContext();

  // Igual que la ficha de usuario: en pantallas angostas los modales grandes ocupan todo el ancho.
  const angosto = typeof window !== 'undefined' && window.innerWidth < 900;

  // Ventas: solo lectura (puede ver inscritos y descargar, sin editar)
  const isVentasOnly = !!(user?.isVentas && !user?.isAdmin && !user?.isCoordinador && !user?.isJefe_recinto);

  useEffect(() => {
    if (visible && taller?._id) loadHorarios();
  }, [visible, taller?._id]);

  // ── API ──────────────────────────────────────────────────────────────────
  const loadHorarios = async () => {
    try {
      setLoading(true);
      const res = await Horarios.GetHorariosPorTaller(taller._id);
      setHorarios(Array.isArray(res.data) ? [...res.data] : Array.isArray(res) ? [...res] : []);
    } catch (e) {
      console.error('Error cargando horarios:', e);
      setHorarios([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarInscritos = async (horarioId, filtros = {}) => {
    setLoadingInscritos(true);
    try {
      const params = { limit: 300 };
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.vigente !== '' && filtros.vigente !== undefined) params.vigente = filtros.vigente;
      const res = await Horarios.GetInscritosPorHorario(horarioId, params);
      if (res) setInscritosData(res);
    } catch (e) {
      notify.error('No se pudieron cargar los inscritos');
    } finally {
      setLoadingInscritos(false);
    }
  };

  const verInscritos = async (horario) => {
    setHorarioSeleccionado(horario);
    setModalInscritosVisible(true);
    setFiltroEstado('');
    setFiltroVigente('');
    await cargarInscritos(horario._id);
  };

  const cerrarModalInscritos = () => {
    setModalInscritosVisible(false);
    setHorarioSeleccionado(null);
    setInscritosData(null);
    setFiltroEstado('');
    setFiltroVigente('');
  };

  const handleFiltroEstado = (val) => {
    setFiltroEstado(val);
    cargarInscritos(horarioSeleccionado._id, { estado: val, vigente: filtroVigente });
  };

  const handleFiltroVigente = (val) => {
    setFiltroVigente(val);
    cargarInscritos(horarioSeleccionado._id, { estado: filtroEstado, vigente: val });
  };

  const confirmarDarDeBaja = async () => {
    setLoadingBaja(true);
    try {
      await InscripcionSvc.DarDeBaja(bajaModal.inscripcion._id);
      notify.success(`${bajaModal.inscripcion.inscrito?.nombre || 'Inscrito'} dado de baja exitosamente`);
      setBajaModal({ visible: false, inscripcion: null });
      await cargarInscritos(horarioSeleccionado._id, { estado: filtroEstado, vigente: filtroVigente });
    } catch (e) {
      notify.error(e?.error || e?.message || 'Error al dar de baja');
    } finally {
      setLoadingBaja(false);
    }
  };

  const abrirCuposModal = (horario) => {
    setCuposModal({ visible: true, horario });
    cuposForm.setFieldsValue({
      cuposIlimitados: horario.cuposIlimitados || false,
      cupos: horario.cuposIlimitados ? undefined : horario.cupos,
      liberarReservados: false,
    });
  };

  const cerrarCuposModal = () => {
    setCuposModal({ visible: false, horario: null });
    cuposForm.resetFields();
  };

  const confirmarAjusteCupos = async () => {
    try {
      const values = await cuposForm.validateFields();
      const { cuposIlimitados, cupos, liberarReservados } = values;

      if (!cuposIlimitados && (!cupos || cupos <= 0)) {
        notify.warning('Ingrese la cantidad de cupos o active cupos ilimitados');
        return;
      }

      const ocp = cuposModal.horario?.cuposOcupados || 0;
      if (!cuposIlimitados && cupos < ocp) {
        notify.warning(`Los cupos no pueden ser menores a los inscritos actuales (${ocp})`);
        return;
      }

      setLoadingCupos(true);
      const payload = {
        cuposIlimitados: cuposIlimitados || false,
        cupos: cuposIlimitados ? undefined : cupos,
        ...(liberarReservados ? { cuposReservados: 0 } : {}),
      };

      await withNotify(Horarios.Update(cuposModal.horario._id, payload), {
        loading: 'Ajustando cupos...',
        success: 'Cupos actualizados exitosamente',
        error: 'No se pudieron actualizar los cupos',
      });

      cerrarCuposModal();
      await loadHorarios();
    } catch (e) {
      if (e.errorFields) return;
      notify.error(e.message || 'Error al ajustar cupos');
    } finally {
      setLoadingCupos(false);
    }
  };

  const abrirCambioModal = async (inscripcion) => {
    setNuevoHorarioId(null);
    setTallerDestinoId('actual');
    setCambioModal({ visible: true, inscripcion });
    setOpcionesCambio({ loading: true, data: null });
    try {
      const res = await InscripcionSvc.OpcionesCambioTaller(inscripcion._id);
      setOpcionesCambio({ loading: false, data: res.data });
    } catch (e) {
      setOpcionesCambio({ loading: false, data: { puedeCambiarTaller: false, motivo: e?.message, talleres: [] } });
    }
  };

  const cerrarCambioModal = () => {
    setCambioModal({ visible: false, inscripcion: null });
    setNuevoHorarioId(null);
    setTallerDestinoId('actual');
    setOpcionesCambio({ loading: false, data: null });
  };

  const elegirTallerDestino = (id) => {
    setTallerDestinoId(id);
    setNuevoHorarioId(null);
  };

  const confirmarCambioHorario = async () => {
    if (!nuevoHorarioId) {
      notify.warning('Selecciona el horario destino');
      return;
    }
    setLoadingCambio(true);
    try {
      const res = await InscripcionSvc.CambiarHorario(cambioModal.inscripcion._id, nuevoHorarioId);
      if (res?.cambio) {
        notify.success(`Cambiado a ${res.cambio.tallerDestinoNombre}. ${textoSaldo(res.cambio)}`);
      } else {
        notify.success('Horario cambiado exitosamente');
      }
      cerrarCambioModal();
      await loadHorarios();
      await cargarInscritos(horarioSeleccionado._id, { estado: filtroEstado, vigente: filtroVigente });
    } catch (e) {
      notify.error(e?.message || 'Error al cambiar el horario');
    } finally {
      setLoadingCambio(false);
    }
  };

  const exportarCSV = async (horarioId) => {
    try {
      setLoadingExport(true);
      notify.info('Generando archivo CSV...');
      await Horarios.ExportarInscritosCSV(horarioId, { vigente: true, estado: 'confirmada' });
      notify.success('CSV descargado exitosamente');
    } catch (e) {
      notify.error('Error al exportar CSV: ' + (e.message || 'Error desconocido'));
    } finally {
      setLoadingExport(false);
    }
  };

  const exportarExcel = async (horarioId) => {
    try {
      setLoadingExport(true);
      notify.info('Generando archivo Excel...');
      await Horarios.ExportarInscritosExcel(horarioId, { vigente: true, estado: 'confirmada' });
      notify.success('Excel descargado exitosamente');
    } catch (e) {
      notify.error('Error al exportar Excel: ' + (e.message || 'Error desconocido'));
    } finally {
      setLoadingExport(false);
    }
  };

  // ── EDICIÓN DE BLOQUES ───────────────────────────────────────────────────
  const startEditExpanded = (record) => {
    setEditingId(record._id);
    setModoEdicionExpandida(true);
    const bloquesActuales = record.bloques?.map((b, idx) => ({
      ...b, id: `existing-${idx}`, isExisting: true,
    })) || [{ dias: record.dias, inicio: record.inicio, fin: record.fin, id: 'existing-0', isExisting: true }];
    setBloquesEdicion(bloquesActuales);
    editForm.setFieldsValue({
      cuposIlimitados: record.cuposIlimitados || false,
      cupos:  record.cuposIlimitados ? undefined : record.cupos,
      nombre: record.nombre,
      sku:    record.sku || null,
    });
  };

  const agregarBloqueEdicion = async () => {
    try {
      const { dias_nuevo, inicio_nuevo, fin_nuevo } = await editForm.validateFields(['dias_nuevo', 'inicio_nuevo', 'fin_nuevo']);
      const hIni = dayjs(inicio_nuevo).format('HH:mm');
      const hFin = dayjs(fin_nuevo).format('HH:mm');
      if (hFin <= hIni) { notify.warning('La hora de fin debe ser posterior a la de inicio'); return; }
      const nuevoBloque = { dias: sortDias(dias_nuevo), inicio: hIni, fin: hFin, id: `new-${Date.now()}`, isExisting: false };
      const validacion = validarSolapamientoBloques([...bloquesEdicion, nuevoBloque]);
      if (validacion.solapa) {
        notify.error({ content: `Solapamiento en ${validacion.diasComunes.join(', ')}: ${validacion.bloqueA.inicio}-${validacion.bloqueA.fin} y ${validacion.bloqueB.inicio}-${validacion.bloqueB.fin}`, duration: 5 });
        return;
      }
      setBloquesEdicion(prev => [...prev, nuevoBloque]);
      editForm.setFieldsValue({ dias_nuevo: undefined, inicio_nuevo: undefined, fin_nuevo: undefined });
      notify.success('Bloque agregado');
    } catch { /* validación form */ }
  };

  const eliminarBloqueEdicion = (bloqueId) => {
    if (bloquesEdicion.length === 1) { notify.warning('Debe mantener al menos un bloque'); return; }
    setBloquesEdicion(prev => prev.filter(b => b.id !== bloqueId));
  };

  const iniciarEdicionBloque = (bloque) => {
    setEditandoBloqueId(bloque.id);
    editForm.setFieldsValue({
      dias_edit:   bloque.dias,
      inicio_edit: dayjs(bloque.inicio, 'HH:mm'),
      fin_edit:    dayjs(bloque.fin,    'HH:mm'),
    });
  };

  const guardarEdicionBloque = async (bloqueId) => {
    try {
      const { dias_edit, inicio_edit, fin_edit } = await editForm.validateFields(['dias_edit', 'inicio_edit', 'fin_edit']);
      const hIni = dayjs(inicio_edit).format('HH:mm');
      const hFin = dayjs(fin_edit).format('HH:mm');
      if (hFin <= hIni) { notify.warning('La hora de fin debe ser posterior a la de inicio'); return; }
      const actualizados = bloquesEdicion.map(b => b.id === bloqueId ? { ...b, dias: sortDias(dias_edit), inicio: hIni, fin: hFin } : b);
      const validacion = validarSolapamientoBloques(actualizados);
      if (validacion.solapa) {
        notify.error({ content: `Solapamiento en ${validacion.diasComunes.join(', ')}: ${validacion.bloqueA.inicio}-${validacion.bloqueA.fin} y ${validacion.bloqueB.inicio}-${validacion.bloqueB.fin}`, duration: 5 });
        return;
      }
      setBloquesEdicion(actualizados);
      setEditandoBloqueId(null);
      editForm.setFieldsValue({ dias_edit: undefined, inicio_edit: undefined, fin_edit: undefined });
      notify.success('Bloque actualizado');
    } catch { /* validación form */ }
  };

  const cancelarEdicionBloque = () => {
    setEditandoBloqueId(null);
    editForm.setFieldsValue({ dias_edit: undefined, inicio_edit: undefined, fin_edit: undefined });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setModoEdicionExpandida(false);
    setBloquesEdicion([]);
    setEditandoBloqueId(null);
    editForm.resetFields();
  };

  const saveEditExpanded = async (horarioId) => {
    try {
      if (bloquesEdicion.length === 0) { notify.warning('Debe tener al menos un bloque'); return; }
      const { cupos, cuposIlimitados, nombre, sku } = await editForm.validateFields(['cupos', 'cuposIlimitados', 'nombre', 'sku']);
      if (!cuposIlimitados && (!cupos || cupos <= 0)) { notify.warning('Ingrese la cantidad de cupos o active cupos ilimitados'); return; }
      const payload = {
        bloques: bloquesEdicion.map(b => ({ dias: b.dias, inicio: b.inicio, fin: b.fin })),
        cuposIlimitados: cuposIlimitados || false,
        cupos: cuposIlimitados ? undefined : cupos,
        nombre: nombre || undefined,
        sku:    sku    || null,
      };
      await withNotify(Horarios.Update(horarioId, payload), {
        loading: 'Actualizando horario...', success: 'Horario actualizado', error: 'No se pudo actualizar el horario',
      });
      setEditingId(null);
      setModoEdicionExpandida(false);
      setBloquesEdicion([]);
      editForm.resetFields();
      await loadHorarios();
    } catch (e) {
      if (e.errorFields) return;
      notify.error(e.message || 'No se pudo actualizar');
    }
  };

  const onDelete = async (horario) => {
    try {
      if (horario._id) {
        await withNotify(Horarios.Delete(horario._id), {
          loading: 'Eliminando horario...', success: 'Horario eliminado', error: 'No se pudo eliminar el horario',
        });
      }
      await loadHorarios();
      if (onHorariosChange) onHorariosChange(horarios.filter(h => h._id !== horario._id));
    } catch (e) {
      notify.error(e.message || 'No se pudo eliminar');
    }
  };

  // ── CREAR HORARIO ────────────────────────────────────────────────────────
  const agregarBloque = async () => {
    try {
      const { dias, inicio, fin } = await createForm.validateFields(['dias', 'inicio', 'fin']);
      const hIni = dayjs(inicio).format('HH:mm');
      const hFin = dayjs(fin).format('HH:mm');
      if (hFin <= hIni) { notify.warning('La hora de fin debe ser posterior a la de inicio'); return; }
      const nuevoBloque = { dias: sortDias(dias), inicio: hIni, fin: hFin, id: Date.now() };
      const validacion  = validarSolapamientoBloques([...bloques, nuevoBloque]);
      if (validacion.solapa) {
        notify.error({ content: `Solapamiento en ${validacion.diasComunes.join(', ')}: ${validacion.bloqueA.inicio}-${validacion.bloqueA.fin} y ${validacion.bloqueB.inicio}-${validacion.bloqueB.fin}`, duration: 5 });
        return;
      }
      setBloques(prev => [...prev, nuevoBloque]);
      createForm.setFieldsValue({ dias: undefined, inicio: undefined, fin: undefined });
      notify.success('Bloque agregado');
    } catch { /* validación form */ }
  };

  const eliminarBloque = (id) => setBloques(prev => prev.filter(b => b.id !== id));

  const crearHorario = async () => {
    if (bloques.length === 0) { notify.warning('Debe agregar al menos un bloque de horario'); return; }
    const cuposIlimitados = createForm.getFieldValue('cuposIlimitados');
    const cupos           = createForm.getFieldValue('cupos');
    if (!cuposIlimitados && (!cupos || cupos <= 0)) { notify.warning('Ingrese la cantidad de cupos o active cupos ilimitados'); return; }
    try {
      setLoading(true);
      const payload = {
        taller: taller._id,
        bloques: bloques.map(b => ({ dias: b.dias, inicio: b.inicio, fin: b.fin })),
        cuposIlimitados: cuposIlimitados || false,
        cupos:   cuposIlimitados ? undefined : cupos,
        nombre:  createForm.getFieldValue('nombre') || undefined,
        sku:     createForm.getFieldValue('sku')    || null,
      };
      await withNotify(Horarios.Post(payload), {
        loading: 'Creando horario...', success: 'Horario creado exitosamente', error: 'No se pudo crear el horario',
      });
      createForm.resetFields();
      setBloques([]);
      await loadHorarios();
      setActiveTab('vista');
    } catch (e) {
      notify.error(e.error || e.message || 'No se pudo crear el horario');
    } finally {
      setLoading(false);
    }
  };

  // ── HELPERS DE RENDER ────────────────────────────────────────────────────

  const bloquesDe = (h) => (h?.bloques?.length > 0
    ? h.bloques
    : [{ dias: h?.dias || [], inicio: h?.inicio, fin: h?.fin }]);

  /** "Lun/Mié 09:00–10:00 · Sáb 11:00–12:00" */
  const textoBloques = (h) => bloquesDe(h)
    .map(b => `${sortDias(b.dias || []).map(d => DIA_ABBR[d] || d).join('/')} ${b.inicio}–${b.fin}`)
    .join(' · ');

  /** Bloques de un horario: chips de días y hora */
  const renderBloques = (horario) => (
    <div className="space-y-1.5">
      {bloquesDe(horario).map((b, i) => (
        <div key={i} className="flex items-center gap-1.5 flex-wrap">
          {sortDias(b.dias || []).map(d => <Chip key={d} tono="azul">{DIA_ABBR[d] || d}</Chip>)}
          <span className="inline-flex items-center gap-1 ml-1 text-sm font-semibold text-gray-700 tabular-nums">
            <FiClock size={13} className="text-primary" /> {b.inicio}–{b.fin}
          </span>
        </div>
      ))}
    </div>
  );

  /** Fila de un bloque dentro de los formularios de crear / editar */
  const filaBloque = (bloque, acciones) => (
    <div key={bloque.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100">
      <div className="flex items-center gap-1.5 flex-wrap">
        {sortDias(bloque.dias).map(d => <Chip key={d} tono="azul">{DIA_ABBR[d] || d}</Chip>)}
        <span className="inline-flex items-center gap-1 ml-1 text-sm font-semibold text-gray-700 tabular-nums">
          <FiClock size={13} className="text-primary" /> {bloque.inicio}–{bloque.fin}
        </span>
      </div>
      <div className="flex items-center gap-1">{acciones}</div>
    </div>
  );

  const totales = horarios.reduce((acc, h) => ({
    cupos: acc.cupos + (h.cuposIlimitados ? 0 : (h.cupos || 0)),
    ilimitado: acc.ilimitado || !!h.cuposIlimitados,
    ocupados: acc.ocupados + (h.cuposOcupados || 0),
    reservados: acc.reservados + (h.cuposReservados || 0),
    disponibles: acc.disponibles + (h.cuposIlimitados ? 0 : Math.max(0, (h.cupos || 0) - (h.cuposOcupados || 0) - (h.cuposReservados || 0))),
  }), { cupos: 0, ilimitado: false, ocupados: 0, reservados: 0, disponibles: 0 });

  // ── TARJETA EN MODO VISTA ────────────────────────────────────────────────
  const renderViewCard = (record) => (
    <article key={record._id} className="p-4 rounded-2xl bg-white border border-gray-100 hover:border-blue-100 transition-colors">
      {/* Qué es: los días y horas mandan; el nombre y el SKU son datos de apoyo */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-start gap-4">
        <div className="min-w-0">
          {renderBloques(record)}
          <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-gray-500">
            <span>{record.nombre ? <>Nombre: <strong className="text-gray-700">{record.nombre}</strong></> : 'Sin nombre'}</span>
            <span aria-hidden>·</span>
            {record.sku
              ? <span>SKU Bsale <strong className="text-gray-700 tabular-nums">{record.sku}</strong></span>
              : (
                <Tooltip title="Sin SKU, las ventas de este horario no se asocian a un producto en Bsale">
                  <span className="cursor-help"><Chip tono="amarillo">Sin SKU de Bsale</Chip></span>
                </Tooltip>
              )}
          </div>
        </div>
        <BarraCupos horario={record} ancho="w-full sm:w-44" />
      </div>

      {/* Acciones:lo de todos los días a la derecha; eliminar aparte, a la izquierda */}
      <div className="flex items-center justify-between gap-2 flex-wrap mt-4 pt-3 border-t border-gray-100">
        <div>
          {!isVentasOnly && (
            <Popconfirm
              title="¿Eliminar este horario?"
              description="Esta acción no se puede deshacer"
              onConfirm={() => onDelete(record)}
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <button type="button" className={claseBtnChicoPeligro}>
                <FiTrash2 size={14} /> Eliminar
              </button>
            </Popconfirm>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isVentasOnly && (
            <>
              <button type="button" onClick={() => startEditExpanded(record)} className={claseBtnChico}>
                <FiEdit2 size={14} /> Editar días y horas
              </button>
              <Tooltip title="Cambiar la cantidad de cupos o liberar reservas atascadas, sin editar el horario">
                <button type="button" onClick={() => abrirCuposModal(record)} className={claseBtnChico}>
                  <FiSliders size={14} /> Ajustar cupos
                </button>
              </Tooltip>
            </>
          )}
          <button type="button" onClick={() => verInscritos(record)} className={claseBtnChicoPrimario}>
            <FiUsers size={14} /> Ver inscritos
          </button>
        </div>
      </div>
    </article>
  );

  // ── TARJETA EN MODO EDICIÓN ──────────────────────────────────────────────
  const renderEditCard = (record) => (
    <article key={record._id} className="p-5 rounded-2xl bg-white border-2 border-primary/30 shadow-[0_8px_24px_-14px_rgba(0,71,187,.35)]">
      <p className="flex items-center gap-2 m-0 mb-4 font-extrabold text-gray-900">
        <FiEdit2 size={15} className="text-primary" /> Editando <span className="hl-amarillo">horario</span>
      </p>

      {/* ── Información básica ── */}
      <div className="mb-5">
        <p className={claseTituloSeccion}>Información básica</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <Form.Item name="nombre" label="Nombre del horario" style={{ margin: 0 }}>
            <Input placeholder="Ej: Horario Matutino" />
          </Form.Item>
          <Form.Item name="sku" label="Código SKU (Bsale)" style={{ margin: 0 }}>
            <Input placeholder="Ej: 6686414024840" />
          </Form.Item>
        </div>
      </div>

      {/* ── Bloques ── */}
      <div className="mb-5">
        <p className={claseTituloSeccion}>Bloques de tiempo</p>
        <div className="space-y-2 mt-2">
          {bloquesEdicion.map((bloque) =>
            editandoBloqueId === bloque.id ? (
              <div key={bloque.id} className="flex gap-2 items-end p-3 rounded-xl bg-blue-50 border border-blue-100">
                <Form.Item name="dias_edit" label="Días" style={{ margin: 0, flex: 2 }}
                  rules={[{ required: true, message: 'Seleccione días' }]}>
                  <Select mode="multiple" options={DIAS_OPTIONS} placeholder="Días" size="small" />
                </Form.Item>
                <Form.Item name="inicio_edit" label="Inicio" style={{ margin: 0, flex: 1 }}
                  rules={[{ required: true, message: 'Requerido' }]}>
                  <DatePicker picker="time" format="HH:mm" size="small" className="w-full" />
                </Form.Item>
                <Form.Item name="fin_edit" label="Fin" style={{ margin: 0, flex: 1 }}
                  rules={[{ required: true, message: 'Requerido' }]}>
                  <DatePicker picker="time" format="HH:mm" size="small" className="w-full" />
                </Form.Item>
                <div className="flex gap-1 mb-0.5">
                  <Tooltip title="Guardar bloque">
                    <button type="button" onClick={() => guardarEdicionBloque(bloque.id)} className={claseBtnIcono}><FiCheck size={16} /></button>
                  </Tooltip>
                  <Tooltip title="Cancelar">
                    <button type="button" onClick={cancelarEdicionBloque} className={claseBtnIcono}><FiX size={16} /></button>
                  </Tooltip>
                </div>
              </div>
            ) : filaBloque(bloque, (
              <>
                <Tooltip title="Editar bloque">
                  <button type="button" onClick={() => iniciarEdicionBloque(bloque)} disabled={editandoBloqueId !== null} className={claseBtnIcono}>
                    <FiEdit2 size={14} />
                  </button>
                </Tooltip>
                <Tooltip title={bloquesEdicion.length === 1 ? 'Debe haber al menos 1 bloque' : 'Eliminar bloque'}>
                  <button type="button" onClick={() => eliminarBloqueEdicion(bloque.id)} disabled={bloquesEdicion.length === 1} className={claseBtnIconoPeligro}>
                    <FiTrash2 size={14} />
                  </button>
                </Tooltip>
              </>
            ))
          )}

          {/* Agregar otro bloque */}
          <div className="p-3 rounded-xl border border-dashed border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-500 m-0 mb-2">Agregar otro bloque</p>
            <div className="flex gap-2 items-end flex-wrap">
              <Form.Item name="dias_nuevo" style={{ margin: 0, flex: '2 1 160px' }}
                rules={[{ required: true, message: 'Seleccione días' }]}>
                <Select mode="multiple" options={DIAS_OPTIONS} placeholder="Días" size="small" />
              </Form.Item>
              <Form.Item name="inicio_nuevo" style={{ margin: 0, flex: '1 1 90px' }}
                rules={[{ required: true, message: 'Requerido' }]}>
                <DatePicker picker="time" format="HH:mm" size="small" className="w-full" placeholder="Inicio" />
              </Form.Item>
              <Form.Item name="fin_nuevo" style={{ margin: 0, flex: '1 1 90px' }}
                rules={[{ required: true, message: 'Requerido' }]}>
                <DatePicker picker="time" format="HH:mm" size="small" className="w-full" placeholder="Fin" />
              </Form.Item>
              <button type="button" onClick={agregarBloqueEdicion} className={claseBtnChico}>
                <FiPlus size={14} /> Agregar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cupos ── */}
      <div className="mb-5">
        <p className={claseTituloSeccion}>Cupos</p>
        <div className="flex items-center gap-4 mt-2 flex-wrap">
          <Form.Item name="cuposIlimitados" valuePropName="checked" style={{ margin: 0 }}>
            <Switch
              checkedChildren="∞ Ilimitado"
              unCheckedChildren="Limitado"
              onChange={(checked) => { if (checked) editForm.setFieldValue('cupos', undefined); }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.cuposIlimitados !== c.cuposIlimitados}>
            {({ getFieldValue }) =>
              getFieldValue('cuposIlimitados') ? (
                <span className="text-sm text-gray-500">Sin límite de inscripciones</span>
              ) : (
                <Form.Item name="cupos" style={{ margin: 0 }}
                  rules={[{ required: true, message: 'Ingrese la cantidad de cupos' }]}>
                  <InputNumber min={1} placeholder="Ej: 24" style={{ width: 120 }} addonAfter="cupos" />
                </Form.Item>
              )
            }
          </Form.Item>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
        <button type="button" onClick={cancelEdit} className={claseBtnSecundario}>Cancelar</button>
        <button type="button" onClick={() => saveEditExpanded(record._id)} className={claseBtnPrimario}>
          <FiCheck size={15} /> Guardar cambios
        </button>
      </div>
    </article>
  );

  // ── TAB: HORARIOS ────────────────────────────────────────────────────────
  const vistaGeneralTab = (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500 m-0">
          Cada horario tiene sus propios cupos e inscritos. Entra a <strong className="text-gray-700">Ver inscritos</strong> para cambiar o dar de baja a una persona.
        </p>
        {!isVentasOnly && horarios.length > 0 && (
          <button type="button" onClick={() => setActiveTab('crear')} className={claseBtnChicoPrimario}>
            <FiPlus size={14} /> Nuevo horario
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center"><Spin size="large" /></div>
      ) : horarios.length === 0 ? (
        <div className="rounded-2xl bg-gray-50">
          <Vacio titulo="Este taller no tiene horarios" texto="Sin horarios nadie puede inscribirse.">
            {!isVentasOnly && (
              <button type="button" onClick={() => setActiveTab('crear')} className={claseBtnPrimario}>
                <FiPlus size={15} /> Crear el primero
              </button>
            )}
          </Vacio>
        </div>
      ) : (
        <Form form={editForm} component={false}>
          <div className="space-y-3">
            {horarios.map(h =>
              editingId === h._id && modoEdicionExpandida
                ? renderEditCard(h)
                : renderViewCard(h)
            )}
          </div>
        </Form>
      )}
    </div>
  );

  // ── TAB: NUEVO HORARIO ───────────────────────────────────────────────────
  const crearHorarioTab = (
    <Form layout="vertical" form={createForm}>
      <div className="space-y-4">
        <section className="p-4 rounded-2xl border border-gray-100">
          <p className={`${claseTituloSeccion} mb-3`}>Información básica</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label="Nombre del horario" name="nombre" tooltip="Ej: Horario Matutino, Tarde" style={{ margin: 0 }}>
              <Input placeholder="Ej: Horario Matutino" />
            </Form.Item>
            <Form.Item label="Código SKU (Bsale)" name="sku" tooltip="Código de asociación con Bsale" style={{ margin: 0 }}>
              <Input placeholder="Ej: 6686414024840" />
            </Form.Item>
          </div>
        </section>

        <section className="p-4 rounded-2xl border border-gray-100">
          <p className={`${claseTituloSeccion} mb-3`}>Bloques de tiempo</p>
          {bloques.length > 0 && (
            <div className="space-y-2 mb-4">
              {bloques.map((bloque) => filaBloque(bloque, (
                <Tooltip title="Quitar bloque">
                  <button type="button" onClick={() => eliminarBloque(bloque.id)} className={claseBtnIconoPeligro}>
                    <FiTrash2 size={14} />
                  </button>
                </Tooltip>
              )))}
            </div>
          )}
          <div className="p-3 rounded-xl border border-dashed border-gray-200">
            <p className="text-xs font-semibold text-gray-500 m-0 mb-2">
              {bloques.length === 0 ? 'Agrega el primer bloque' : 'Agregar otro bloque'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Form.Item label="Días" name="dias" style={{ margin: 0 }}
                rules={[{ required: true, message: 'Seleccione días' }]}>
                <Select mode="multiple" options={DIAS_OPTIONS} placeholder="Ej: Lunes, Miércoles" />
              </Form.Item>
              <Form.Item label="Hora inicio" name="inicio" style={{ margin: 0 }}
                rules={[{ required: true, message: 'Requerido' }]}>
                <DatePicker picker="time" format="HH:mm" className="w-full" placeholder="Ej: 10:00" />
              </Form.Item>
              <Form.Item label="Hora fin" name="fin" style={{ margin: 0 }}
                rules={[{ required: true, message: 'Requerido' }]}>
                <DatePicker picker="time" format="HH:mm" className="w-full" placeholder="Ej: 12:00" />
              </Form.Item>
              <Form.Item label=" " style={{ margin: 0 }}>
                <button type="button" onClick={agregarBloque} className={`${claseBtnChico} w-full justify-center !py-[7px]`}>
                  <FiPlus size={14} /> Agregar
                </button>
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="p-4 rounded-2xl border border-gray-100">
          <p className={`${claseTituloSeccion} mb-3`}>Cupos</p>
          <div className="flex items-center gap-6 flex-wrap">
            <Form.Item name="cuposIlimitados" valuePropName="checked" label="Cupos ilimitados"
              tooltip="Sin límite de inscripciones" style={{ margin: 0 }}>
              <Switch onChange={(checked) => { if (checked) createForm.setFieldValue('cupos', undefined); }} />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(p, c) => p.cuposIlimitados !== c.cuposIlimitados}>
              {({ getFieldValue }) =>
                getFieldValue('cuposIlimitados') ? (
                  <Chip tono="azul">Sin límite de inscripciones</Chip>
                ) : (
                  <Form.Item label="Cantidad de cupos" name="cupos" style={{ margin: 0 }}
                    rules={[{ required: true, message: 'Ingrese la cantidad de cupos' }]}>
                    <InputNumber min={1} placeholder="Ej: 20" style={{ width: 160 }} addonAfter="cupos" />
                  </Form.Item>
                )
              }
            </Form.Item>
          </div>
        </section>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => { createForm.resetFields(); setBloques([]); }} className={claseBtnSecundario}>
            Limpiar
          </button>
          <button type="button" onClick={crearHorario} disabled={bloques.length === 0 || loading} className={claseBtnPrimario}>
            {loading ? <LoadingOutlined /> : <FiPlus size={15} />} Crear horario
          </button>
        </div>
      </div>
    </Form>
  );

  // ── COLUMNAS INSCRITOS ───────────────────────────────────────────────────
  const ESTADO_INSCRIPCION = {
    confirmada: ['azul', 'Confirmada'],
    pendiente: ['amarillo', 'Pendiente'],
    cancelada: ['rojo', 'Cancelada'],
  };

  // Seis columnas para que entren las acciones sin desplazarse: la persona junta
  // nombre, RUT y correo; el plan junta plan y para quién; el estado junta estado y
  // vigencia.
  const columnasInscritos = [
    {
      title: 'Persona',
      dataIndex: ['inscrito', 'nombre'],
      key: 'nombre',
      sorter: (a, b) => (a.inscrito?.nombre || '').localeCompare(b.inscrito?.nombre || ''),
      render: (v, r) => (
        <div className="flex items-center gap-3 min-w-0 max-w-[280px]">
          <Iniciales nombre={v} />
          <div className="min-w-0">
            <p className="font-bold text-gray-900 m-0 truncate">{formatearNombre(v)}</p>
            <p className="text-xs text-gray-500 m-0 truncate" title={r.inscrito?.email}>
              <span className="tabular-nums">{r.inscrito?.rut}</span>
              {r.inscrito?.email && <> · {r.inscrito.email}</>}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: 'Salud', key: 'salud', width: 140,
      filters: [
        { text: 'Con condición declarada', value: 'con' },
        { text: 'Sin condición',           value: 'sin' },
      ],
      onFilter: (value, r) => tieneCondicionSalud(r.inscrito?.enfermedadesBases) === (value === 'con'),
      render: (_, r) => {
        const condicion = r.inscrito?.enfermedadesBases;
        if (!tieneCondicionSalud(condicion)) return <span className="text-xs text-gray-400">Sin condición</span>;
        return (
          <Tooltip title={condicion}>
            <span className="inline-flex max-w-[130px]">
              <Chip tono="amarillo" icon={<MedicineBoxOutlined />} className="truncate">{condicion}</Chip>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Plan', key: 'plan', width: 120,
      render: (_, r) => (
        <div>
          <span className="capitalize"><ChipNeutro>{r.plan?.nombre}</ChipNeutro></span>
          <p className="text-[11px] text-gray-500 m-0 mt-1">{r.tipoInscripcion === 'para_mi' ? 'Para sí mismo' : 'Para un familiar'}</p>
        </div>
      ),
    },
    {
      title: 'Estado', key: 'estado', width: 170,
      render: (_, r) => {
        const [tono, label] = ESTADO_INSCRIPCION[r.estado] || ['gris', r.estado];
        return (
          <div>
            <Chip tono={tono}>{label}</Chip>
            <p className="text-[11px] text-gray-500 m-0 mt-1 tabular-nums whitespace-nowrap">
              {r.estado === 'cancelada'
                ? 'Sin cupo'
                : `${r.vigente ? 'Vigente' : 'Vencida'}${r.duracion?.fechaTermino ? ` · hasta ${dayjs(r.duracion.fechaTermino).format('DD/MM/YY')}` : ''}`}
            </p>
          </div>
        );
      },
    },
    {
      title: 'Pagado', dataIndex: 'montoTotal', key: 'montoTotal', align: 'right', width: 90,
      render: (m) => <span className="font-semibold tabular-nums">${(m || 0).toLocaleString('es-CL')}</span>,
    },
    ...(isVentasOnly ? [] : [{
      title: 'Acciones', key: 'acciones', width: 210, align: 'right',
      render: (_, r) => r.estado !== 'cancelada' ? (
        <div className="inline-flex items-center gap-1.5">
          <Tooltip title="Mover a otro horario de este taller, o a otro taller">
            <button type="button" onClick={() => abrirCambioModal(r)} className={claseBtnChico}>
              <FiRepeat size={13} /> Cambiar
            </button>
          </Tooltip>
          <button type="button" onClick={() => setBajaModal({ visible: true, inscripcion: r })} className={claseBtnChicoPeligro}>
            <FiUserX size={13} /> Dar de baja
          </button>
        </div>
      ) : null,
    }]),
  ];

  // Estilos de los modales con cabecera azul: el contenido va pegado al borde.
  const estilosModalFicha = {
    content: { padding: 0, overflow: 'hidden', borderRadius: 24 },
    body: { padding: 0, background: '#f4f6f9' },
  };

  const periodoTaller = taller?.actividadesInicio && taller?.actividadesFin
    ? `${dayjs(taller.actividadesInicio).format('DD/MM')} – ${dayjs(taller.actividadesFin).format('DD/MM/YYYY')}`
    : null;

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Modal principal: horarios del taller ─────────────────────────── */}
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        closable={false}
        title={null}
        width={angosto ? '100%' : 880}
        destroyOnHidden
        styles={estilosModalFicha}
      >
        {taller && (
          <>
            <CabeceraModal
              icon={<FiCalendar size={24} />}
              titulo={taller.nombre}
              subtitulo={taller.recinto?.nombre}
              onClose={onClose}
              chips={(
                <>
                  <ChipCabecera activo={taller.inscripcionesEstado === 'abiertas'}>
                    Inscripciones {taller.inscripcionesEstado === 'abiertas' ? 'abiertas' : 'pausadas'}
                  </ChipCabecera>
                  {periodoTaller && (
                    <span className="inline-flex items-center gap-1 text-xs text-blue-100">
                      <FiCalendar size={12} /> Actividades {periodoTaller}
                    </span>
                  )}
                </>
              )}
              datos={[
                {
                  label: 'Ocupación',
                  value: totales.cupos > 0 ? `${Math.min(100, Math.round(((totales.ocupados + totales.reservados) / totales.cupos) * 100))}%` : '—',
                  sub: totales.ilimitado ? 'sin contar horarios sin límite' : null,
                },
                { label: 'Cupos disponibles', value: totales.ilimitado && totales.cupos === 0 ? '∞' : totales.disponibles },
              ]}
            />

            <div className="p-5 sm:p-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
                <Kpi icon={<FiCalendar size={17} />} label="Horarios" value={horarios.length} ok={horarios.length > 0} />
                <Kpi icon={<FiGrid size={17} />} label="Cupos totales" value={totales.ilimitado && totales.cupos === 0 ? '∞' : totales.cupos} ok={totales.cupos > 0 || totales.ilimitado} />
                <Kpi icon={<FiUsers size={17} />} label="Inscritos" value={totales.ocupados} ok={totales.ocupados > 0} />
                <Kpi icon={<FiClock size={17} />} label="Reservas en curso" value={totales.reservados} ok={false} alerta={totales.reservados > 0} />
              </div>

              <section className="rounded-3xl bg-white border border-gray-100 px-5 pb-5">
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={[
                    {
                      key: 'vista',
                      label: <Etiqueta icon={<FiCalendar size={15} />} texto="Horarios" n={horarios.length} />,
                      children: vistaGeneralTab,
                    },
                    ...(isVentasOnly ? [] : [{
                      key: 'crear',
                      label: <Etiqueta icon={<FiPlus size={15} />} texto="Nuevo horario" />,
                      children: crearHorarioTab,
                    }]),
                  ]}
                />
              </section>
            </div>
          </>
        )}
      </Modal>

      {/* ── Modal inscritos ──────────────────────────────────────────────── */}
      <Modal
        open={modalInscritosVisible}
        onCancel={cerrarModalInscritos}
        footer={null}
        closable={false}
        title={null}
        width={angosto ? '100%' : 1100}
        destroyOnHidden
        styles={estilosModalFicha}
      >
        {horarioSeleccionado && (
          <CabeceraModal
            icon={<FiUsers size={24} />}
            titulo={horarioSeleccionado.nombre || textoBloques(horarioSeleccionado)}
            subtitulo={`Inscritos de este horario · ${horarioSeleccionado.nombre ? `${textoBloques(horarioSeleccionado)} · ` : ''}${taller?.nombre || ''}`}
            onClose={cerrarModalInscritos}
            datos={inscritosData ? [
              { label: 'Cupos ocupados', value: inscritosData.cuposInfo.cuposOcupados ?? 0 },
              {
                label: 'Cupos disponibles',
                value: inscritosData.cuposInfo.cuposIlimitados ? '∞' : inscritosData.cuposInfo.cuposDisponibles,
                sub: inscritosData.cuposInfo.cuposIlimitados ? 'sin límite' : `de ${inscritosData.cuposInfo.cuposTotal}`,
              },
            ] : []}
          />
        )}

        <div className="p-5 sm:p-6">
          {loadingInscritos ? (
            <div className="py-16 text-center"><Spin size="large" /></div>
          ) : inscritosData ? (
            <>
              {/* Indicadores del horario completo (no dependen de los filtros) */}
              {(() => {
                const porEstado = inscritosData.estadisticas?.porEstado || {};
                const vigentes = inscritosData.estadisticas?.porVigencia?.vigentes || 0;
                return (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
                    <Kpi icon={<FiCheck size={17} />} label="Confirmadas" value={porEstado.confirmada || 0} ok={(porEstado.confirmada || 0) > 0} />
                    <Kpi icon={<FiClock size={17} />} label="Pendientes de pago" value={porEstado.pendiente || 0} ok={false} alerta={(porEstado.pendiente || 0) > 0} />
                    <Kpi icon={<FiUserX size={17} />} label="Canceladas" value={porEstado.cancelada || 0} ok={false} />
                    <Kpi icon={<FiCalendar size={17} />} label="Vigentes" value={vigentes} ok={vigentes > 0} />
                  </div>
                );
              })()}

              {/* Filtros y exportación */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
                <span className={claseTituloSeccion}>Mostrar</span>
                <FiltroChips
                  label="Estado"
                  valor={filtroEstado}
                  onChange={handleFiltroEstado}
                  opciones={[
                    { value: '', label: 'Todos' },
                    { value: 'confirmada', label: 'Confirmadas' },
                    { value: 'pendiente', label: 'Pendientes' },
                    { value: 'cancelada', label: 'Canceladas' },
                  ]}
                />
                <FiltroChips
                  label="Vigencia"
                  valor={filtroVigente}
                  onChange={handleFiltroVigente}
                  opciones={[
                    { value: '', label: 'Todas' },
                    { value: 'true', label: 'Vigentes' },
                    { value: 'false', label: 'No vigentes' },
                  ]}
                />
                {/* La exportación no sigue los filtros: siempre baja la lista de asistencia */}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-gray-500">Lista de confirmados vigentes:</span>
                  <button type="button" onClick={() => exportarExcel(horarioSeleccionado._id)} disabled={loadingExport} className={claseBtnChico}>
                    <FiDownload size={14} /> Excel
                  </button>
                  <button type="button" onClick={() => exportarCSV(horarioSeleccionado._id)} disabled={loadingExport} className={claseBtnChico}>
                    <FiFileText size={14} /> CSV
                  </button>
                </div>
              </div>

              <div className="admin-table-card">
                <Table
                  dataSource={inscritosData.inscritos}
                  columns={columnasInscritos}
                  rowKey="_id"
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} inscrito${t !== 1 ? 's' : ''}` }}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: <Vacio titulo="Sin inscritos" texto="No hay inscripciones para los filtros elegidos." /> }}
                />
              </div>
            </>
          ) : (
            <Alert message="No se pudieron cargar los inscritos" type="warning" showIcon />
          )}
        </div>
      </Modal>

      {/* ── Modal confirmación dar de baja ──────────────────────────────── */}
      <Modal
        open={bajaModal.visible}
        onCancel={() => setBajaModal({ visible: false, inscripcion: null })}
        width={480}
        destroyOnHidden
        title={<>Dar de <span className="hl-amarillo">baja</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setBajaModal({ visible: false, inscripcion: null })} className={claseBtnSecundario}>Volver</button>
            <button type="button" onClick={confirmarDarDeBaja} disabled={loadingBaja} className={claseBtnPeligro}>
              {loadingBaja ? <LoadingOutlined /> : <FiUserX size={15} />} Dar de baja
            </button>
          </div>
        }
      >
        {bajaModal.inscripcion && (
          <>
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-100 mb-4">
              <Iniciales nombre={bajaModal.inscripcion.inscrito?.nombre} />
              <div className="min-w-0">
                <p className="font-bold text-gray-900 m-0 truncate">{formatearNombre(bajaModal.inscripcion.inscrito?.nombre)}</p>
                <p className="text-xs text-gray-500 m-0 tabular-nums">{bajaModal.inscripcion.inscrito?.rut}</p>
              </div>
              <span className="ml-auto capitalize"><ChipNeutro>{bajaModal.inscripcion.plan?.nombre}</ChipNeutro></span>
            </div>
            <Alert type="warning" showIcon message="Cancela la inscripción y libera su cupo en el horario"
              description="No devuelve el pago: si corresponde, se gestiona aparte." />
          </>
        )}
      </Modal>

      {/* ── Modal ajustar cupos ──────────────────────────────────────────── */}
      <Modal
        open={cuposModal.visible}
        onCancel={cerrarCuposModal}
        width={500}
        destroyOnHidden
        title={<>Ajustar <span className="hl-amarillo">cupos</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cerrarCuposModal} className={claseBtnSecundario}>Volver</button>
            <button type="button" onClick={confirmarAjusteCupos} disabled={loadingCupos} className={claseBtnPrimario}>
              {loadingCupos && <LoadingOutlined />} Guardar cupos
            </button>
          </div>
        }
      >
        {cuposModal.horario && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 m-0">
              <strong>{textoBloques(cuposModal.horario)}</strong>{cuposModal.horario.nombre && <> · {cuposModal.horario.nombre}</>}
              <br /><span className="text-xs text-gray-500">Cambia cuántas personas caben en este horario. No puede quedar bajo los inscritos actuales.</span>
            </p>

            <div className="grid grid-cols-3 gap-2.5">
              {[
                { label: 'Total', value: cuposModal.horario.cuposIlimitados ? '∞' : (cuposModal.horario.cupos || 0) },
                { label: 'Inscritos', value: cuposModal.horario.cuposOcupados || 0 },
                { label: 'En reserva', value: cuposModal.horario.cuposReservados || 0, alerta: (cuposModal.horario.cuposReservados || 0) > 0 },
              ].map(d => (
                <div key={d.label} className={`p-3 rounded-2xl text-center border ${d.alerta ? 'bg-[#fffbea] border-secondary' : 'bg-gray-50 border-gray-100'}`}>
                  <p className="text-2xl font-extrabold text-gray-900 leading-none m-0 tabular-nums">{d.value}</p>
                  <p className="text-[11px] font-semibold text-gray-500 m-0 mt-1">{d.label}</p>
                </div>
              ))}
            </div>

            <Form form={cuposForm} layout="vertical" size="middle">
              <Form.Item
                name="cuposIlimitados"
                valuePropName="checked"
                label="Cupos ilimitados"
                tooltip="Activa esto si no quieres límite de inscripciones"
                style={{ marginBottom: 12 }}
              >
                <Switch onChange={(checked) => { if (checked) cuposForm.setFieldValue('cupos', undefined); }} />
              </Form.Item>

              <Form.Item noStyle shouldUpdate={(p, c) => p.cuposIlimitados !== c.cuposIlimitados}>
                {({ getFieldValue }) =>
                  !getFieldValue('cuposIlimitados') && (
                    <Form.Item
                      label="Nueva cantidad de cupos"
                      name="cupos"
                      rules={[{ required: true, message: 'Ingrese la cantidad de cupos' }]}
                      extra={(cuposModal.horario.cuposOcupados || 0) > 0
                        ? `Mínimo ${cuposModal.horario.cuposOcupados}: los inscritos actuales.`
                        : null}
                    >
                      <InputNumber min={cuposModal.horario.cuposOcupados || 1} style={{ width: '100%' }} addonAfter="cupos" placeholder="Ej: 20" />
                    </Form.Item>
                  )
                }
              </Form.Item>

              {(cuposModal.horario.cuposReservados || 0) > 0 && (
                <Form.Item
                  name="liberarReservados"
                  valuePropName="checked"
                  label="Liberar reservas atascadas"
                  tooltip="Los cupos reservados quedan en ese estado cuando un proceso de pago no completa. Activar esto los libera inmediatamente."
                  extra={`Libera ${cuposModal.horario.cuposReservados} cupo${cuposModal.horario.cuposReservados !== 1 ? 's' : ''} bloqueado${cuposModal.horario.cuposReservados !== 1 ? 's' : ''}.`}
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              )}
            </Form>
          </div>
        )}
      </Modal>

      {/* ── Modal cambio de horario o taller ─────────────────────────────── */}
      <Modal
        open={cambioModal.visible}
        onCancel={cerrarCambioModal}
        width={580}
        destroyOnHidden
        title={<>Cambiar <span className="hl-amarillo">{tallerDestinoId === 'actual' ? 'horario' : 'de taller'}</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cerrarCambioModal} className={claseBtnSecundario}>Volver</button>
            <button type="button" onClick={confirmarCambioHorario} disabled={!nuevoHorarioId || loadingCambio} className={claseBtnPrimario}>
              {loadingCambio ? <LoadingOutlined /> : <FiRepeat size={15} />}
              {tallerDestinoId === 'actual' ? 'Confirmar cambio' : 'Confirmar cambio de taller'}
            </button>
          </div>
        }
      >
        {cambioModal.inscripcion && (() => {
          const ins = cambioModal.inscripcion;
          const opciones = opcionesCambio.data;
          const tallerDestino = tallerDestinoId === 'actual'
            ? null
            : opciones?.talleres?.find(t => t._id === tallerDestinoId);

          // Horarios destino: con cupos y distintos al actual. Los de otro taller
          // ya vienen filtrados por cupo desde la API.
          const horariosDestino = tallerDestino
            ? tallerDestino.horarios
            : horarios.filter(h => {
                if (h._id === (ins.horario?._id || ins.horario)) return false;
                if (h.cuposIlimitados) return true;
                const disp = (h.cupos || 0) - (h.cuposOcupados || 0) - (h.cuposReservados || 0);
                return disp > 0;
              });

          return (
            <div className="space-y-4">
              {/* Persona */}
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                <Iniciales nombre={ins.inscrito?.nombre} />
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 m-0 truncate">{formatearNombre(ins.inscrito?.nombre)}</p>
                  <p className="text-xs text-gray-500 m-0 tabular-nums">
                    {ins.inscrito?.rut}
                    {ins.horario && <> · ahora en {textoBloques(ins.horario)}</>}
                  </p>
                </div>
              </div>

              {/* Taller destino */}
              <div>
                <Paso n={1} titulo="¿A qué taller?" ayuda="Deja el mismo taller para cambiar solo el horario. Los demás muestran el saldo que quedaría." />
                <Select
                  value={tallerDestinoId}
                  onChange={elegirTallerDestino}
                  loading={opcionesCambio.loading}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  optionLabelProp="label"
                >
                  <Select.Option value="actual" label={`${taller?.nombre || 'Este taller'} (mismo taller)`}>
                    <span className="text-[13px]">{taller?.nombre || 'Este taller'}</span>
                    <span className="text-xs text-gray-500"> · cambiar solo el horario</span>
                  </Select.Option>
                  {(opciones?.talleres || []).map(t => (
                    <Select.Option key={t._id} value={t._id} label={t.nombre}>
                      <div className="flex justify-between items-center gap-2">
                        <div className="min-w-0">
                          <p className="text-[13px] m-0 truncate">{t.nombre}</p>
                          {t.recinto && <p className="text-[11px] text-gray-500 m-0">{t.recinto}</p>}
                        </div>
                        <Chip tono={TONO_SALDO[t.sentido]}>
                          {t.sentido === 'sin_diferencia' ? '= $0' : `${t.saldo > 0 ? '+' : '−'}${pesos(t.saldo)}`}
                        </Chip>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
                {opciones && !opciones.puedeCambiarTaller && (
                  <p className="text-xs text-gray-500 m-0 mt-1.5">No se puede cambiar a otro taller: {opciones.motivo}</p>
                )}
                {opciones?.puedeCambiarTaller && opciones.talleres.length === 0 && (
                  <p className="text-xs text-gray-500 m-0 mt-1.5">No hay otros talleres con plan mensual y cupos disponibles.</p>
                )}
              </div>

              {/* Horario destino */}
              {horariosDestino.length === 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message="No hay otros horarios disponibles"
                  description="No existen horarios con cupos disponibles en este taller."
                />
              ) : (
                <div>
                  <Paso n={2} titulo="¿A qué horario?" ayuda="Solo aparecen horarios con cupos libres." />
                  <Select
                    value={nuevoHorarioId}
                    onChange={setNuevoHorarioId}
                    placeholder="Elige un horario..."
                    style={{ width: '100%' }}
                    optionLabelProp="label"
                  >
                    {horariosDestino.map(h => {
                      const disp = h.cuposIlimitados
                        ? '∞'
                        : `${(h.cupos || 0) - (h.cuposOcupados || 0) - (h.cuposReservados || 0)} disp.`;
                      const label = textoBloques(h);
                      return (
                        <Select.Option key={h._id} value={h._id} label={label}>
                          <div className="flex justify-between items-center gap-2">
                            <div className="min-w-0">
                              {h.nombre && <p className="text-xs font-bold m-0">{h.nombre}</p>}
                              <p className="text-[13px] m-0">{label}</p>
                            </div>
                            <Chip tono="azul">{disp}</Chip>
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
              )}
              {/* Saldo del cambio de taller (solo administración) */}
              {tallerDestino && <Paso n={3} titulo="Revisa la diferencia de valor" ayuda="Solo la ve la administración." />}
              {tallerDestino && (
                <div className="-mt-2 p-4 rounded-2xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04)] text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Pagado por el cupo actual</span>
                    <span className="font-semibold tabular-nums">{pesos(tallerDestino.montoPagado)}</span>
                  </div>
                  <div className="flex justify-between gap-3 mt-1.5">
                    <span className="text-gray-500">Cupo mensual en {tallerDestino.nombre} ({tallerDestino.tipoPrecioDestino})</span>
                    <span className="font-semibold tabular-nums">{pesos(tallerDestino.precioDestino)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                    <span className="font-bold text-gray-900">{SALDO[tallerDestino.sentido]?.label}</span>
                    <Chip tono={TONO_SALDO[tallerDestino.sentido]} className="!text-sm">{pesos(tallerDestino.saldo)}</Chip>
                  </div>
                  {tallerDestino.fechaTermino && (
                    <p className="flex items-center gap-1.5 text-xs text-gray-500 m-0 mt-3">
                      <FiCalendar size={12} /> Nueva vigencia: {dayjs(tallerDestino.fechaInicio).format('DD/MM/YYYY')} → {dayjs(tallerDestino.fechaTermino).format('DD/MM/YYYY')}
                    </p>
                  )}
                  <p className="text-[11px] text-gray-400 m-0 mt-1">Solo informativo: no se cobra, no se devuelve ni se emite boleta.</p>
                </div>
              )}

            </div>
          );
        })()}
      </Modal>
    </>
  );
};

export default HorariosModal;
